import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2, ArrowUpRight, Flag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { updateCountdowns } from "@/lib/api";
import { Reveal } from "./SectionHeading";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

const toInput = (iso) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

function Timer({ label, date, testid }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const ms = Math.max(0, new Date(date).getTime() - now);
  const units = [
    [Math.floor(ms / 86400000), "DAYS", "text-neon"],
    [Math.floor(ms / 3600000) % 24, "HRS", "text-neonpurple"],
    [Math.floor(ms / 60000) % 60, "MIN", "text-hotpink"],
    [Math.floor(ms / 1000) % 60, "SEC", "text-white"],
  ];

  return (
    <div
      data-testid={testid}
      className="flex-1 rounded-3xl border border-white/10 bg-panel p-7 transition-colors duration-300 hover:border-neon md:p-10"
    >
      <div className="flex items-baseline justify-between gap-4">
        <p className="font-jbmono text-[11px] uppercase tracking-[0.25em] text-white/60">
          {label}
        </p>
        <p className="font-jbmono text-[10px] tracking-[0.2em] text-white/30">
          {new Date(date)
            .toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
            .toUpperCase()}
        </p>
      </div>
      <div className="mt-6 flex items-start gap-2 sm:gap-4">
        {units.map(([v, l, c], i) => (
          <div key={l} className="flex items-start gap-2 sm:gap-4">
            <div className="text-center">
              <div
                data-testid={`${testid}-${l.toLowerCase()}`}
                className={`font-bebas text-5xl leading-none sm:text-7xl md:text-8xl ${c}`}
              >
                {pad(v)}
              </div>
              <div className="mt-2 font-jbmono text-[9px] tracking-[0.3em] text-white/40">
                {l}
              </div>
            </div>
            {i < 3 && (
              <span className="blink pt-1 font-bebas text-4xl text-white/20 sm:text-6xl">
                :
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StateCard({ title, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-1 flex-col items-center justify-center rounded-3xl border border-dashed border-white/15 bg-panel/50 p-10 text-center"
    >
      <Flag className="mb-4 h-6 w-6 text-hotpink" />
      <p className="font-spray text-3xl text-outline-neon sm:text-4xl">{title}</p>
      <p className="mt-3 max-w-xs font-jbmono text-[10px] uppercase tracking-[0.25em] text-white/40">
        {sub}
      </p>
    </div>
  );
}

export default function Countdown({ countdowns, milestones = [], gcProfile, scheduleWatch, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    tournamentLabel: "",
    playoffsLabel: "",
    tournament: "",
    playoffs: "",
    ms3Label: "",
    ms3: "",
    passcode: "",
  });

  const chain = (milestones.length
    ? milestones
    : [countdowns.tournament, countdowns.playoffs]
  )
    .filter((m) => m.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const upcoming = chain.filter((m) => new Date(m.date).getTime() > Date.now());
  const first = upcoming[0] || null;
  const second = upcoming[1] || null;
  const seasonDone = chain.length > 0 && upcoming.length === 0;

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        tournamentLabel: countdowns.tournament.label,
        playoffsLabel: countdowns.playoffs.label,
        tournament: toInput(countdowns.tournament.date),
        playoffs: toInput(countdowns.playoffs.date),
        ms3Label: milestones[2]?.label || "",
        ms3: milestones[2]?.date ? toInput(milestones[2].date) : "",
      }));
    }
  }, [open, countdowns, milestones]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateCountdowns({
        passcode: form.passcode,
        tournament_date: new Date(form.tournament).toISOString(),
        playoffs_date: new Date(form.playoffs).toISOString(),
        tournament_label: form.tournamentLabel,
        playoffs_label: form.playoffsLabel,
        milestone3_label: form.ms3Label || null,
        milestone3_date: form.ms3 ? new Date(form.ms3).toISOString() : null,
      });
      toast.success("Countdowns updated. Clock is ticking.");
      setOpen(false);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full border border-white/15 bg-black px-3 py-2.5 font-jbmono text-xs text-white outline-none transition-colors focus:border-neon";
  const label = "mb-1.5 block font-jbmono text-[10px] uppercase tracking-[0.25em] text-white/50";

  return (
    <section
      id="countdowns"
      data-testid="countdown-section"
      className="mx-auto max-w-7xl px-6 py-24 md:py-32"
    >
      <Reveal>
        <div className="mb-10 flex items-end justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="h-2 w-2 animate-pulse bg-hotpink" />
            <h2 className="font-spray text-4xl tracking-wide sm:text-5xl">
              NEXT UP
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="autosync-status"
              className={`hidden items-center gap-2 rounded-full border px-4 py-2 font-jbmono text-[9px] tracking-[0.2em] md:flex ${
                scheduleWatch?.detected
                  ? "animate-pulse border-neon text-neon"
                  : "border-white/15 text-white/40"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  scheduleWatch?.detected ? "bg-neon" : "bg-white/30"
                }`}
              />
              {scheduleWatch?.detected
                ? "12UP LIVE ON GC — SET THE DATES"
                : "AUTO-SYNC: WATCHING GC FOR 12UP"}
            </span>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="countdown-edit-button"
                className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-jbmono text-[10px] tracking-[0.25em] text-white/60 transition-colors hover:border-neon hover:text-neon"
              >
                <Settings2 className="h-3.5 w-3.5" />
                UPDATE DATES
              </button>
            </DialogTrigger>
            <DialogContent className="border-white/15 bg-[#0A0A0E] text-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bebas text-3xl tracking-wide">
                  UPDATE COUNTDOWNS
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className={label}>Tournament name</label>
                  <input
                    data-testid="countdown-form-tournament-label"
                    className={field}
                    value={form.tournamentLabel}
                    onChange={(e) =>
                      setForm({ ...form, tournamentLabel: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={label}>Tournament date</label>
                  <input
                    data-testid="countdown-form-tournament-date"
                    type="datetime-local"
                    required
                    className={field}
                    value={form.tournament}
                    onChange={(e) =>
                      setForm({ ...form, tournament: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={label}>Playoffs name</label>
                  <input
                    data-testid="countdown-form-playoffs-label"
                    className={field}
                    value={form.playoffsLabel}
                    onChange={(e) =>
                      setForm({ ...form, playoffsLabel: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={label}>Playoffs date</label>
                  <input
                    data-testid="countdown-form-playoffs-date"
                    type="datetime-local"
                    required
                    className={field}
                    value={form.playoffs}
                    onChange={(e) =>
                      setForm({ ...form, playoffs: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={label}>Next milestone name (optional)</label>
                  <input
                    data-testid="countdown-form-ms3-label"
                    className={field}
                    placeholder="Fall Ball"
                    value={form.ms3Label}
                    onChange={(e) =>
                      setForm({ ...form, ms3Label: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={label}>Next milestone date (optional)</label>
                  <input
                    data-testid="countdown-form-ms3-date"
                    type="datetime-local"
                    className={field}
                    value={form.ms3}
                    onChange={(e) => setForm({ ...form, ms3: e.target.value })}
                  />
                </div>
                <div>
                  <label className={label}>Family passcode</label>
                  <input
                    data-testid="countdown-form-passcode"
                    type="password"
                    required
                    className={field}
                    value={form.passcode}
                    onChange={(e) =>
                      setForm({ ...form, passcode: e.target.value })
                    }
                  />
                </div>
                <button
                  data-testid="countdown-form-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-full bg-neon py-3 font-bebas text-xl tracking-widest text-black transition-shadow hover:shadow-[0_0_25px_rgba(57,255,20,0.5)] disabled:opacity-50"
                >
                  {busy ? "SAVING..." : "SAVE DATES"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </Reveal>
      <div className="flex flex-col gap-4 lg:flex-row">
        <Reveal className="flex flex-1">
          {first ? (
            <Timer label={first.label} date={first.date} testid="countdown-tournament" />
          ) : (
            <StateCard
              title={seasonDone ? "SEASON IN THE BOOKS" : "DATES TBD"}
              sub={seasonDone ? "What a year, Big Cal. See you next season." : "Set the next milestone via Update Dates"}
              testid="countdown-state-1"
            />
          )}
        </Reveal>
        <Reveal delay={0.12} className="flex flex-1">
          {second ? (
            <Timer label={second.label} date={second.date} testid="countdown-playoffs" />
          ) : (
            <StateCard
              title={seasonDone ? "OFFSEASON GRIND" : "ONE MILESTONE LEFT"}
              sub={seasonDone ? "The cage never closes." : "Add another milestone via Update Dates"}
              testid="countdown-state-2"
            />
          )}
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <a
          data-testid="gc-schedule-link"
          href={gcProfile}
          target="_blank"
          rel="noreferrer"
          className="group mt-6 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-panel px-6 py-5 transition-colors duration-300 hover:border-neon"
        >
          <div>
            <p className="font-bebas text-2xl leading-none tracking-wide">
              SEATTLE SELECT SCHEDULE
            </p>
            <p className="mt-1 font-jbmono text-[10px] uppercase tracking-[0.2em] text-white/40">
              Games stream & update on Big Cal's GameChanger profile
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-2 font-jbmono text-[11px] tracking-[0.25em] text-neon">
            OPEN GAMECHANGER
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </span>
        </a>
      </Reveal>
    </section>
  );
}
